import { Checkbox } from '@fluentui/react-components';
import { Attach16Regular } from '@fluentui/react-icons';
import { get } from 'lodash';
import moment from 'moment-timezone';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { isToday, isYesterday } from '../Utils';
import { EmailItem } from './Email.interface';
import { getDateLabel } from './Email.utils';
import './EmailsOverviewList.scss';

export interface EmailsOverviewListProps {
  emails: EmailItem[];
  emailSelected: (email: EmailItem) => void;
  selectedEmailId?: string | null;
  selectedEmailIds?: string[];
  onSelectionChange?: (emailIds: string[]) => void;
}

interface EmailsOverviewListItemProps {
  email: EmailItem;
  selected: boolean;
  checked: boolean;
  selectable: boolean;
  onClick: (email: EmailItem) => void;
  onCheckedChange: (email: EmailItem, checked: boolean) => void;
}

export const getEmailIds = (emails: EmailItem[]): string[] =>
  emails.flatMap((email) => (email.id ? [email.id] : []));

const EmailsOverviewListItem: React.FC<EmailsOverviewListItemProps> = ({
  email,
  selected,
  checked,
  selectable,
  onClick,
  onCheckedChange,
}) => {
  const { t } = useTranslation();
  const classNames = ['emailListItem'];

  if (!email.isRead) {
    classNames.push('unread');
  }

  if (selected) {
    classNames.push('selected');
  }

  if (selectable) {
    classNames.push('selectable');
  }

  const handleActivate = () => onClick(email);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleActivate();
    }
  };

  const subject =
    get(email, 'subject', false) ||
    '(' + t('email_no_subject', { defaultValue: 'Kein Betreff' }) + ')';

  return (
    <div className={classNames.join(' ')}>
      {selectable && (
        <Checkbox
          className="emailListItemCheckbox"
          checked={checked}
          onChange={(_event, data) => onCheckedChange(email, data.checked === true)}
          aria-label={t('email_list_item_select_aria_label', {
            defaultValue: 'E-Mail auswählen: {{subject}}',
            subject,
          })}
        />
      )}
      <div
        className="emailListItemContent"
        role="button"
        tabIndex={0}
        aria-pressed={selected}
        onClick={handleActivate}
        onKeyDown={handleKeyDown}
      >
        <div className="topRow">
          <span className="name">
            {email.isDraft && (
              <span className="draftLabel">[{t('email_draft', { defaultValue: 'Entwurf' })}]</span>
            )}
            {!email.isDraft &&
              (get(email, 'sender.emailAddress.name') ||
                '(' + t('email_no_sender', { defaultValue: 'Kein Absender' }) + ')')}
          </span>
          {email.hasAttachments && (
            <span>
              <Attach16Regular aria-hidden="true" />
            </span>
          )}
        </div>
        <div className="subjectWrapper">
          <span className="subject">{subject}</span>
          <span className="date">{getDateLabel(email)}</span>
        </div>
        <div className="bodyPreviewWrapper">
          <div className="bodyPreview">{email.bodyPreview}</div>
        </div>
      </div>
    </div>
  );
};

const EmailsOverviewList: React.FC<EmailsOverviewListProps> = ({
  emails,
  emailSelected,
  selectedEmailId = null,
  selectedEmailIds = [],
  onSelectionChange,
}) => {
  const { t } = useTranslation();

  /**
   * @note Function needs to be declared inline, otherwise
   * the amount of useTranslation() calls will differ in each
   * render and react will throw an error
   */
  const getSortIndex = (email: EmailItem): string => {
    const sentDate = get(email, 'sentDateTime', '');
    const momentDate = moment(sentDate);

    if (isToday(momentDate.toDate())) {
      return t('today', { defaultValue: 'Heute' });
    }

    if (isYesterday(momentDate.toDate())) {
      return t('yesteday', { defaultValue: 'Gestern' });
    }

    return momentDate.format('DD.MM.YY');
  };

  const sortByDay = (emails: EmailItem[]): { [key: string]: EmailItem[] } => {
    const sorted: { [key: string]: EmailItem[] } = {};

    for (const email of emails) {
      const index = getSortIndex(email);

      if (!(index in sorted)) {
        sorted[index] = [];
      }

      sorted[index].push(email);
    }

    return sorted;
  };

  const selectable = onSelectionChange !== undefined;
  const sorted = sortByDay(emails);
  const allIds = getEmailIds(emails);
  const selectedCount = allIds.filter((id) => selectedEmailIds.includes(id)).length;

  const handleCheckedChange = (email: EmailItem, checked: boolean) => {
    if (!onSelectionChange || !email.id) {
      return;
    }

    const emailId = email.id;
    onSelectionChange(
      checked ? [...selectedEmailIds, emailId] : selectedEmailIds.filter((id) => id !== emailId),
    );
  };

  const handleSelectAllChange = (checked: boolean) => {
    if (!onSelectionChange) {
      return;
    }

    if (checked) {
      const missing = allIds.filter((id) => !selectedEmailIds.includes(id));
      onSelectionChange([...selectedEmailIds, ...missing]);
      return;
    }

    onSelectionChange(selectedEmailIds.filter((id) => !allIds.includes(id)));
  };

  const getSelectAllState = (): boolean | 'mixed' => {
    if (selectedCount === 0) {
      return false;
    }

    return selectedCount === allIds.length ? true : 'mixed';
  };

  return (
    <>
      {selectable && emails.length > 0 && (
        <div className="emailListSelectAll">
          <Checkbox
            data-testid="emailListSelectAll"
            checked={getSelectAllState()}
            onChange={(_event, data) => handleSelectAllChange(data.checked === true)}
            label={t('email_select_all', {
              defaultValue: 'Alle auswählen ({{total}})',
              total: allIds.length,
            })}
          />
        </div>
      )}
      <div className="emailList">
        {Object.keys(sorted).map((key, index) => (
          <div key={`wrapper_${index}`}>
            <div className="dateDivider" key={key}>
              {key}
            </div>
            {sorted[key].map((email, index) => (
              <EmailsOverviewListItem
                key={`email_list_item_${index}_${email.id}`}
                email={email}
                selected={selectedEmailId !== null && selectedEmailId === email.id}
                checked={email.id !== undefined && selectedEmailIds.includes(email.id)}
                selectable={selectable}
                onClick={emailSelected}
                onCheckedChange={handleCheckedChange}
              />
            ))}
          </div>
        ))}
      </div>
    </>
  );
};

export default EmailsOverviewList;
